/**
 * Collection Launch Experience — Operation WOW.
 *
 * A premium collector-first presentation layer that sits ABOVE the existing
 * album-management surface. Purely presentational — reads the canonical
 * `/api/collector-auth/dashboard-summary` payload and derives every value
 * from real data. No new schemas, no fake progress, no dead buttons.
 *
 * Sections rendered:
 *   1. Collector identity hero (Founding Collector, verified, stats)
 *   2. Collection overview (résumé-style list)
 *   3. Current Mission (top in-progress album, real progress)
 *   4. Hall of Fame (real completed albums)
 *   5. Future Vision preview (4 "In Development" cards — no functionality)
 *   6. Founder closing message ("Thank you for believing in CollectorUp")
 *
 * The launch badge is a presentation-only marker isolated in the
 * `useFoundingCollector` hook so it can later be wired to a canonical
 * backend entitlement without rewriting the surface.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Trophy, Award, Sparkles, BookOpen, Compass,
  ArrowRight, Package, Users, Rocket, Clock, Lock,
} from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import { friendlyAlbumName } from "@/lib/certificateFormat";
import { deriveAlbumStatus } from "@/lib/albumStatus";
import { AlbumStatusPill, AlbumProgressBar, AlbumNicknameEditor } from "@/components/collector/AlbumCardBits";
import { buildAlbumPresentation } from "@/lib/albumPresentation";
import CollectionAlbumSnapshotCard from "@/components/collector/CollectionAlbumSnapshotCard";

/* ────────────────────────────────────────────────────────────────
 * Founding Collector flag — presentation-only.
 *
 * Every verified collector active during the launch window is treated
 * as a Founding Collector. The window and eligibility rule are isolated
 * here so a future backend entitlement can override without rewriting
 * every surface.
 * ────────────────────────────────────────────────────────────────*/
export const FOUNDING_LAUNCH_WINDOW_END = "2027-01-01";

/**
 * @param {object} args
 * @param {string|null} args.memberSince  ISO date the collector joined.
 * @param {boolean} args.emailVerified
 * @param {number}  args.certificateCount
 * @param {boolean} args.hasClaimed       for anonymous cert previews
 * @returns {boolean}
 */
export function isFoundingCollector({ memberSince, emailVerified, certificateCount, hasClaimed }) {
  // Certificate holders always qualify — every launch cohort member.
  if (certificateCount && certificateCount > 0) return true;
  if (hasClaimed) return true;
  // Verified collectors who joined during the launch window qualify.
  if (emailVerified && memberSince) {
    try {
      return new Date(memberSince) < new Date(FOUNDING_LAUNCH_WINDOW_END);
    } catch { /* fallthrough */ }
  }
  return false;
}

/* ────────────────────────────────────────────────────────────────
 * Data hook — one HTTP call, canonical dashboard-summary.
 * Exposes `refresh` so mutations (e.g. nickname edits) can pull the
 * latest snapshot without reloading the whole page.
 * ────────────────────────────────────────────────────────────────*/
export function useDashboardSummary() {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const load = React.useCallback(() => {
    setState((s) => ({ ...s, loading: true }));
    return api.get("/collector-auth/dashboard-summary")
      .then((r) => { setState({ loading: false, data: r.data, error: null }); return r.data; })
      .catch((e) => { setState({ loading: false, data: null, error: e }); throw e; });
  }, []);
  useEffect(() => {
    let cancelled = false;
    api.get("/collector-auth/dashboard-summary")
      .then((r) => { if (!cancelled) setState({ loading: false, data: r.data, error: null }); })
      .catch((e) => { if (!cancelled) setState({ loading: false, data: null, error: e }); });
    return () => { cancelled = true; };
  }, []);
  return { ...state, refresh: load };
}

/* ────────────────────────────────────────────────────────────────
 * PUBLIC — LaunchTopSections: hero + overview + current mission only.
 *
 * Used by the new museum layout on MyCollection so the top of the page
 * stays visually locked while the sections BELOW get restructured.
 * ────────────────────────────────────────────────────────────────*/
export function LaunchTopSections({ collector }) {
  const { loading, data, refresh } = useDashboardSummary();
  if (loading) return <SkeletonHero />;
  if (!data) return null;

  const c = data.collector || {};
  const s = data.summary || {};
  const founding = isFoundingCollector({
    memberSince: collector?.created_at,
    emailVerified: c.email_verified,
    certificateCount: s.certificates || 0,
  });
  return (
    <div className="space-y-8" data-testid="launch-top">
      <CollectorHero collector={collector} summary={s} identity={c} founding={founding} />
      <CollectionOverview
        continueCollecting={data.continue_collecting || []}
        completed={data.completed_albums || []}
        specials={data.special_collectibles || []}
        onAlbumChanged={refresh}
      />
      <CurrentMission continueCollecting={data.continue_collecting || []} />
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
 * PUBLIC — main export (kept for surfaces that still want the full
 * launch experience: unchanged behaviour).
 * ────────────────────────────────────────────────────────────────*/
export default function CollectionLaunchExperience({ collector }) {
  const { loading, data, refresh } = useDashboardSummary();

  if (loading) return <SkeletonHero />;
  if (!data) return null;

  const c = data.collector || {};
  const s = data.summary || {};
  const founding = isFoundingCollector({
    memberSince: collector?.created_at,
    emailVerified: c.email_verified,
    certificateCount: s.certificates || 0,
  });

  return (
    <div className="space-y-8" data-testid="launch-experience">
      <CollectorHero collector={collector} summary={s} identity={c} founding={founding} />
      <CollectionOverview
        continueCollecting={data.continue_collecting || []}
        completed={data.completed_albums || []}
        specials={data.special_collectibles || []}
        onAlbumChanged={refresh}
      />
      <CurrentMission continueCollecting={data.continue_collecting || []} />
      <HallOfFame completed={data.completed_albums || []} />
      <FutureVisionPreview />
      <FounderClosing name={c.first_name || c.display_name} />
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
 * 1. Collector Identity Hero
 * ────────────────────────────────────────────────────────────────*/
function CollectorHero({ collector, summary, identity, founding }) {
  const displayName = identity?.display_name || collector?.display_name || collector?.first_name || "Collector";
  const publicId = identity?.public_collector_id || collector?.public_collector_id;
  const username = collector?.username;
  const avatarUrl = collector?.avatar_media_id && username
    ? `${API}/public/collectors/${encodeURIComponent(username)}/avatar?v=${encodeURIComponent(collector?.avatar_updated_at || "")}`
    : null;
  const memberSince = collector?.created_at
    ? new Date(collector.created_at).getFullYear()
    : null;
  // Phase C2a — customer-facing Lifetime Completion tile removed.
  // Backend `overall_completion` remains for external consumers.

  return (
    <section
      data-testid="launch-hero"
      className="relative overflow-hidden rounded-3xl border border-[#c8a24d]/25 bg-gradient-to-br from-[#0b1220] via-[#0e1a2e] to-[#0a0f1c] text-white shadow-[0_30px_80px_-30px_rgba(0,0,0,0.6)]"
    >
      {/* Ambient gold glow */}
      <div aria-hidden className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full"
           style={{ background: "radial-gradient(closest-side, rgba(200,162,77,0.28), transparent 70%)" }} />
      <div aria-hidden className="pointer-events-none absolute -bottom-32 -left-16 h-96 w-96 rounded-full"
           style={{ background: "radial-gradient(closest-side, rgba(34,197,94,0.16), transparent 70%)" }} />

      <div className="relative p-6 md:p-10">
        {/* Top strip: verified pill + Founding badge */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-3 py-1 text-[10px] uppercase tracking-[0.24em] font-bold text-emerald-300"
                data-testid="launch-hero-verified">
            <ShieldCheck size={11} /> Verified Collector
          </span>
          {founding && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#c8a24d]/50 bg-[#c8a24d]/12 px-3 py-1 text-[10px] uppercase tracking-[0.24em] font-bold text-[#e4c989]"
                  data-testid="launch-hero-founding-badge"
                  title="Founding Collector — one of the first verified collectors on CollectorUp.">
              <Sparkles size={11} /> Founding Collector
            </span>
          )}
          <span className="text-[10px] uppercase tracking-[0.24em] text-white/50" data-testid="launch-hero-welcome-eyebrow">
            Welcome to Your Digital Collector Home
          </span>
        </div>

        {/* Identity row */}
        <div className="mt-6 flex flex-col md:flex-row md:items-end gap-6">
          <div className="shrink-0">
            <div className="h-24 w-24 md:h-28 md:w-28 rounded-full overflow-hidden ring-2 ring-[#c8a24d]/40 bg-white/5 grid place-items-center"
                 data-testid="launch-hero-avatar">
              {avatarUrl ? (
                <img src={avatarUrl} alt="" className="h-full w-full object-cover"
                     onError={(e) => { e.currentTarget.style.display = "none"; }} />
              ) : (
                <span className="font-display text-4xl text-[#e4c989]">
                  {(displayName || "?").slice(0, 1).toUpperCase()}
                </span>
              )}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h1
              data-testid="launch-hero-name"
              className="font-display font-bold text-white leading-[0.95] text-4xl md:text-6xl lg:text-7xl break-words"
              style={{ letterSpacing: "-0.02em" }}
            >
              {displayName}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-white/60">
              {publicId && (
                <span className="font-mono font-bold px-2 py-0.5 rounded-full bg-[#c8a24d]/15 text-[#e4c989]"
                      data-testid="launch-hero-public-id">
                  {publicId}
                </span>
              )}
              {memberSince && (
                <span data-testid="launch-hero-member-since">Collector Since {memberSince}</span>
              )}
              <span>· Officially Verified</span>
            </div>
            <p className="mt-4 max-w-xl text-sm md:text-base text-white/70 leading-relaxed">
              Your verified album certificate is now part of your permanent CollectorUp profile —
              one collector, one lifetime of collecting.
            </p>
          </div>
        </div>

        {/* Stat strip — Phase C2a canonical 4-tile snapshot */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="launch-hero-stats">
          <StatCell label="Albums Owned" value={summary?.albums_owned ?? 0} accent="gold" testid="stat-owned" />
          <StatCell label="Completed" value={summary?.albums_completed ?? 0} accent="emerald" testid="stat-completed" />
          <StatCell label="In Progress" value={summary?.albums_in_progress ?? 0} accent="emerald" testid="stat-in-progress" />
          <StatCell label="Certificates" value={summary?.certificates ?? 0} accent="gold" testid="stat-certificates" />
        </div>
      </div>
    </section>
  );
}

function StatCell({ label, value, accent, testid }) {
  const border = accent === "gold" ? "border-[#c8a24d]/25" : "border-emerald-400/25";
  const glow = accent === "gold" ? "shadow-[inset_0_1px_0_rgba(200,162,77,0.15)]" : "shadow-[inset_0_1px_0_rgba(52,211,153,0.15)]";
  const num = accent === "gold" ? "text-[#e4c989]" : "text-emerald-300";
  return (
    <div className={`rounded-2xl border ${border} bg-white/[0.03] p-4 backdrop-blur-sm ${glow}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-white/50">{label}</div>
      <div className={`mt-1 font-display font-bold text-3xl md:text-4xl ${num}`} data-testid={`${testid}-value`}>
        {value}
      </div>
    </div>
  );
}

function SkeletonHero() {
  return (
    <div className="rounded-3xl border border-white/10 bg-[#0b1220] p-10 animate-pulse space-y-4" data-testid="launch-hero-loading">
      <div className="h-4 w-40 rounded bg-white/10" />
      <div className="h-16 w-2/3 rounded bg-white/10" />
      <div className="h-4 w-1/3 rounded bg-white/10" />
      <div className="grid grid-cols-4 gap-3 mt-6">
        {Array.from({length:4}).map((_,i) => <div key={i} className="h-20 rounded-2xl bg-white/10" />)}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
 * 2. Collection Overview — profile-facing "Your World Cup Collection".
 *    Phase 2 · Each row is a full album card with large title, cover-
 *    design subtitle, editable nickname, status pill, progress bar
 *    and percentage. "Ownership tracked" fallback removed — every
 *    card either has a percent or renders the "Album Only" pill.
 * ────────────────────────────────────────────────────────────────*/
function CollectionOverview({ continueCollecting, completed, specials, onAlbumChanged }) {
  const rows = useMemo(() => {
    const list = [
      ...completed.map((a) => ({ ...a, _kind: "completed" })),
      ...continueCollecting.map((a) => ({ ...a, _kind: "in_progress" })),
    ];
    return list.slice(0, 12);
  }, [continueCollecting, completed]);

  if (rows.length === 0 && specials.length === 0) return null;

  return (
    <section className="rounded-3xl border-2 border-[var(--ink)] bg-white shadow-[6px_6px_0_var(--ink)] p-4 sm:p-6 md:p-8"
             data-testid="launch-overview">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--panini-red)]">Your Collection</div>
          <h2 className="font-display text-2xl md:text-3xl mt-1">
            Your World Cup Collection
          </h2>
        </div>
        <div className="text-sm text-[var(--smoke)]" data-testid="launch-overview-count">
          {rows.length} album{rows.length === 1 ? "" : "s"}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-[minmax(0,1fr)] gap-4" data-testid="launch-overview-list">
        {rows.map((a) => (
          <OverviewRow key={a.id} album={a} onAlbumChanged={onAlbumChanged} />
        ))}
        {specials.length > 0 && (
          <div className="mt-1 py-3 px-4 flex items-center justify-between gap-3 rounded-2xl bg-neutral-50 border border-[var(--stone)]"
               data-testid="launch-overview-specials">
            <div>
              <div className="font-bold text-[var(--ink)]">Special Collectibles</div>
              <div className="text-xs text-[var(--smoke)]">{specials.length} item{specials.length === 1 ? "" : "s"} · Vault</div>
            </div>
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[var(--smoke)]">Vault</span>
          </div>
        )}
      </div>
    </section>
  );
}

function OverviewRow({ album, onAlbumChanged }) {
  const saveNickname = async (next) => {
    try {
      await api.patch(`/collector-auth/my-collection/albums/${album.id}`, {
        nickname: next || null,
      });
      toast.success(next ? "Nickname updated." : "Nickname cleared.");
      if (onAlbumChanged) onAlbumChanged();
    } catch (err) {
      const msg = formatApiError(err?.response?.data?.detail) || "Could not save nickname.";
      throw new Error(msg);
    }
  };

  return (
    <div data-testid={`launch-overview-row-${album.id}`}>
      <CollectionAlbumSnapshotCard
        album={album}
        layout="horizontal"
        showCounts={false}
        onNicknameSave={saveNickname}
      />
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
 * 3. Current Mission — top in-progress album.
 * ────────────────────────────────────────────────────────────────*/
function CurrentMission({ continueCollecting }) {
  const mission = useMemo(() => {
    if (!continueCollecting.length) return null;
    // Prefer highest completion% below 100 with tracking enabled; fallback to first row (most recent updated).
    const tracked = continueCollecting.filter((a) => a.completion && a.completion_tracking_enabled !== false);
    if (tracked.length) {
      return tracked.slice().sort((a, b) =>
        (Number(b.completion?.completion_percentage || 0) - Number(a.completion?.completion_percentage || 0))
      )[0];
    }
    return continueCollecting[0];
  }, [continueCollecting]);

  if (!mission) return null;

  const mp = buildAlbumPresentation(mission);
  const title = mp.officialTitle || friendlyAlbumName(mission.public_slug) || "Album";
  const subtitle = mp.coverLabel || "";
  const comp = mission.completion || {};
  const pct = Math.round(Number(comp.completion_percentage || 0));
  const remaining = Math.max(0, Number(comp.total_stickers || 0) - Number(comp.owned_effective || 0));
  const hasStickerCount = Number(comp.total_stickers || 0) > 0;

  return (
    <section
      data-testid="launch-current-mission"
      className="rounded-3xl border border-[#c8a24d]/25 bg-gradient-to-br from-[#0b1220] to-[#0e1a2e] text-white p-6 md:p-8 shadow-[0_20px_60px_-25px_rgba(0,0,0,0.6)]"
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-bold text-[#e4c989]">
        <Rocket size={12} /> Current Mission
      </div>
      <div className="mt-4 grid md:grid-cols-[1fr_auto] gap-6 items-center">
        <div>
          <h2 className="font-display font-bold text-3xl md:text-4xl leading-tight" data-testid="launch-mission-title">
            {title}
          </h2>
          {subtitle && <div className="mt-1 text-sm text-white/60">{subtitle}</div>}
          <div className="mt-4 flex items-baseline gap-3">
            <div className="font-display font-bold text-5xl md:text-6xl text-emerald-300" data-testid="launch-mission-pct">
              {pct}%
            </div>
            <div className="text-xs uppercase tracking-[0.22em] text-white/50">Complete</div>
          </div>
          {hasStickerCount && (
            <div className="mt-2 text-sm text-white/75" data-testid="launch-mission-counts">
              {Number(comp.owned_effective || 0)} / {Number(comp.total_stickers || 0)} Stickers
              {remaining > 0 && (
                <span className="text-white/50"> · {remaining} to go</span>
              )}
            </div>
          )}
          <div className="mt-4 h-2 w-full rounded-full bg-white/10 overflow-hidden" role="progressbar"
               aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
            <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-300 transition-all"
                 style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to={`/dashboard/my-collection/albums/${mission.id}`}
              data-testid="launch-mission-continue"
              className="inline-flex items-center gap-2 rounded-2xl bg-[#c8a24d] text-[#0a0f1c] px-5 py-3 text-sm font-display uppercase tracking-widest hover:brightness-110 transition"
            >
              Continue Collecting <ArrowRight size={15} />
            </Link>
          </div>
        </div>
        {mission.cover_image_url && (
          <div className="mx-auto md:mx-0 w-40 md:w-52 aspect-[3/4] rounded-2xl overflow-hidden bg-white/5 border border-white/10 grid place-items-center">
            <img src={mission.cover_image_url} alt="" className="max-w-full max-h-full w-auto h-auto object-contain object-center"
                 data-testid="launch-mission-cover" />
          </div>
        )}
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────
 * 4. Hall of Fame — real completed albums.
 * ────────────────────────────────────────────────────────────────*/
function HallOfFame({ completed }) {
  if (completed.length === 0) {
    return (
      <section className="rounded-3xl border-2 border-dashed border-[var(--ink)] bg-white p-8 text-center"
               data-testid="launch-hall-of-fame-empty">
        <div className="mx-auto w-14 h-14 rounded-full bg-[var(--panini-yellow-soft)] grid place-items-center">
          <Trophy size={26} className="text-[var(--ink)]" />
        </div>
        <div className="mt-3 text-[10px] uppercase tracking-[0.28em] font-bold text-[#c8a24d]">Hall of Fame</div>
        <div className="mt-2 font-display text-2xl">Your first trophy is waiting.</div>
        <p className="mt-2 text-sm text-[var(--smoke)] max-w-md mx-auto">
          Complete an album to unlock the Hall of Fame — a gold-touched trophy room for your verified achievements.
        </p>
      </section>
    );
  }

  return (
    <section data-testid="launch-hall-of-fame">
      <div className="flex items-baseline justify-between mb-4">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-bold text-[#c8a24d]">
          <Trophy size={13} /> Hall of Fame
        </div>
        <div className="text-xs text-[var(--smoke)]">{completed.length} completed</div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {completed.slice(0, 6).map((a) => <TrophyCard key={a.id} album={a} />)}
      </div>
    </section>
  );
}

function TrophyCard({ album }) {
  const p = buildAlbumPresentation(album);
  return (
    <article
      className="relative rounded-2xl border-2 border-[#c8a24d]/60 bg-gradient-to-br from-white to-[#fffaf0] p-4 shadow-[0_10px_30px_-15px_rgba(200,162,77,0.55)] overflow-visible"
      data-testid={`launch-trophy-${album.id}`}
      data-status={p.status}
    >
      <div className="absolute -top-2 -right-2 rounded-full bg-[#c8a24d] text-[#0a0f1c] p-1.5 shadow-md">
        <Trophy size={14} strokeWidth={2.5} />
      </div>
      <div className="aspect-[3/4] bg-neutral-50 rounded-lg overflow-hidden grid place-items-center border border-[#c8a24d]/25">
        {album.cover_image_url ? (
          <img src={album.cover_image_url} alt=""
               className="max-w-full max-h-full w-auto h-auto object-contain object-center"
               data-testid={`launch-trophy-cover-${album.id}`} />
        ) : (
          <Package size={36} className="text-[var(--smoke)]" />
        )}
      </div>
      <div className="mt-3">
        <div
          className="font-display text-lg leading-tight text-[var(--ink)] break-words"
          data-testid="album-official-title"
        >
          {p.officialTitle}
        </div>
        {p.coverLabel && (
          <div
            className="mt-0.5 text-xs text-[var(--smoke)] truncate"
            data-testid="album-cover-label"
          >
            {p.coverLabel}
          </div>
        )}
        <div
          className="mt-2 inline-flex items-center gap-1 whitespace-nowrap text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-800"
          data-testid="album-completed-badge"
        >
          <Trophy size={11} strokeWidth={2.5} /> 100% Complete
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-[#c8a24d]/20 flex items-center justify-between">
        <Link
          to={p.openAlbumHref || `/dashboard/my-collection/albums/${album.id}`}
          data-testid={`launch-trophy-view-${album.id}`}
          className="text-xs font-bold uppercase tracking-[0.22em] text-[var(--panini-red)] hover:underline"
        >
          View Album
        </Link>
        {album.certificate_number && (
          <Link
            to={`/certificates/${album.certificate_number}`}
            className="text-xs font-bold uppercase tracking-[0.22em] text-[var(--ink)] hover:underline"
          >
            Certificate ↗
          </Link>
        )}
      </div>
    </article>
  );
}

/* ────────────────────────────────────────────────────────────────
 * 5. Future Vision Preview — 4 In-Development cards.
 * ────────────────────────────────────────────────────────────────*/
export function FutureVisionPreview() {
  const cards = [
    {
      icon: BookOpen,
      title: "Your Lifetime Collection",
      body: "Add albums from past and future tournaments — 2022 Qatar, 2026 CAN/MEX/USA, and every collection that becomes part of your journey.",
      bullets: ["Albums from every tournament", "Multiple album variants", "One permanent collector profile", "Preserved over time"],
      accent: "gold",
    },
    {
      icon: Users,
      title: "Smart Trading & Collector Matching",
      body: "Add your missing sticker list and your duplicate list. CollectorUp will match you with nearby collectors who have what you need — and who need what you have.",
      bullets: ["Missing-list tracking", "Duplicate-list tracking", "Two-way match recommendations", "Nearby collector discovery", "Privacy-conscious location"],
      accent: "emerald",
    },
    {
      icon: Package,
      title: "Collector Vault",
      body: "Showcase premium items, rare stickers, treasure boxes, special editions, signed memorabilia, and the collectibles that make your collection unique.",
      bullets: ["Swiss Treasure Boxes", "Signed memorabilia", "Limited-edition albums", "Premium collectibles"],
      accent: "gold",
    },
    {
      icon: Award,
      title: "Achievements & Collection Journey",
      body: "Celebrate completed albums, certificates, collecting milestones, and the story of your collection across the years.",
      bullets: ["Hall of Fame", "Achievement badges", "Collection timeline", "Verified certificates"],
      accent: "emerald",
    },
  ];
  return (
    <section
      data-testid="launch-future-vision"
      className="rounded-3xl border border-white/10 bg-gradient-to-br from-[#0b1220] to-[#0e1a2e] text-white p-6 md:p-10"
    >
      <div className="text-center">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-bold text-[#e4c989]">
          <Compass size={12} /> What&apos;s Coming to CollectorUp
        </div>
        <p className="mt-4 max-w-2xl mx-auto text-sm md:text-base text-white/70">
          Your profile will continue growing as CollectorUp evolves. Every card below is currently in development —
          launching in an upcoming CollectorUp update.
        </p>
      </div>
      <div className="mt-8 grid gap-4 md:grid-cols-2">
        {cards.map((c, i) => <FutureCard key={i} {...c} />)}
      </div>
    </section>
  );
}

function FutureCard({ icon: Icon, title, body, bullets, accent }) {
  const border = accent === "gold" ? "border-[#c8a24d]/30" : "border-emerald-400/25";
  const iconTone = accent === "gold" ? "text-[#e4c989]" : "text-emerald-300";
  return (
    <article className={`rounded-2xl border ${border} bg-white/[0.03] p-5 md:p-6`} data-testid={`future-card-${title.replace(/\s+/g,"-").toLowerCase()}`}>
      <div className="flex items-start gap-3">
        <div className={`shrink-0 h-10 w-10 rounded-xl grid place-items-center bg-white/5 border ${border}`}>
          <Icon size={18} className={iconTone} aria-hidden />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-bold text-lg md:text-xl text-white leading-tight">{title}</h3>
            <span className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/5 px-2 py-0.5 text-[9px] uppercase tracking-[0.24em] font-bold text-white/70"
                  aria-label="In development"
                  data-testid="future-card-in-dev-badge">
              <Clock size={10} /> In Development
            </span>
          </div>
          <p className="mt-2 text-sm text-white/70 leading-relaxed">{body}</p>
          <ul className="mt-3 space-y-1 text-xs text-white/60">
            {bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2">
                <Sparkles size={11} className={`mt-0.5 ${iconTone}`} aria-hidden />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </article>
  );
}

/* ────────────────────────────────────────────────────────────────
 * 6. Founder closing message — thank you.
 * ────────────────────────────────────────────────────────────────*/
export function FounderClosing({ name, publicVariant = false }) {
  if (publicVariant) {
    return (
      <section
        data-testid="launch-founder-closing-public"
        className="rounded-3xl border border-[#c8a24d]/25 bg-gradient-to-br from-[#0b1220] to-[#0e1a2e] text-white p-6 md:p-10 text-center"
      >
        <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#e4c989]">A word from Gil</div>
        <h2 className="mt-3 font-display text-2xl md:text-3xl">
          A verified CollectorUp collector.
        </h2>
        <p className="mt-4 max-w-2xl mx-auto text-sm md:text-base text-white/70 leading-relaxed">
          Every verified collector on CollectorUp helps us build the future digital home for collectors around the world.
        </p>
        <p className="mt-3 text-xs uppercase tracking-[0.28em] font-bold text-[#e4c989]">— Gil Maakaroun, Founder</p>
      </section>
    );
  }
  return (
    <section
      data-testid="launch-founder-closing"
      className="rounded-3xl border border-[#c8a24d]/25 bg-gradient-to-br from-[#0b1220] to-[#0e1a2e] text-white p-6 md:p-10 text-center"
    >
      <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#e4c989]">Thank you</div>
      <h2 className="mt-3 font-display text-2xl md:text-4xl">
        Thank you for believing in CollectorUp{ name ? `, ${name}` : "" }.
      </h2>
      <p className="mt-4 max-w-2xl mx-auto text-sm md:text-base text-white/70 leading-relaxed">
        You&apos;re helping us build the future digital home for collectors around the world.
        As one of our first verified collectors, your profile will continue gaining new features as CollectorUp evolves.
      </p>
      <p className="mt-4 text-xs uppercase tracking-[0.28em] font-bold text-[#e4c989]">— Gil Maakaroun, Founder</p>
    </section>
  );
}
